import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CalendarCheck, MessageSquare, PartyPopper, CheckCheck } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

const ICONS = {
  booking_request: CalendarCheck,
  booking_confirmed: CalendarCheck,
  booking_cancelled: CalendarCheck,
  new_message: MessageSquare,
  event_rsvp: PartyPopper,
};

export function NotificationBell() {
  const { t } = useTranslation();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-xl"
          data-testid="header-notifications-button"
          aria-label={t("header.notifications")}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -end-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--brand-primary)] px-1 text-[10px] font-semibold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t("header.notifications")}</span>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllRead()}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              data-testid="notifications-mark-read-button"
            >
              <CheckCheck className="h-3 w-3" /> {t("header.markAllRead")}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="max-h-80">
          {items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("header.noNotifications")}</p>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <DropdownMenuItem
                  key={n.notification_id}
                  className={`flex items-start gap-3 py-3 cursor-pointer ${!n.is_read ? "bg-accent/60" : ""}`}
                  onClick={() => {
                    markRead(n.notification_id);
                    if (n.link) navigate(n.link);
                    setOpen(false);
                  }}
                  data-testid="notification-item"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm leading-snug">{n.content}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </span>
                  </span>
                  {!n.is_read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[color:var(--brand-primary)]" />}
                </DropdownMenuItem>
              );
            })
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
